import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import DegreeSelect, { OTHERS_VALUE as DEGREE_OTHERS } from "@/components/DegreeSelect";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";

const CONTACT_RE = /^[1-9][0-9]{9}$/;

// Detect standard applicant columns from the admin's field labels/types so
// backend duplicate detection + admin filters work without any autogenerated
// fields being added to the form.
function extractStandard(fields, responses) {
    const out = {
        name: "",
        email: "",
        contact_number: "",
        college_name: "",
        year_of_graduation: "",
        degree: "",
        course: "",
    };
    (fields || []).forEach((f) => {
        const lbl = (f.label || "").toLowerCase();
        const raw = responses[f.label];
        const val =
            raw && typeof raw === "object" && "value" in raw
                ? raw.value === "Others"
                    ? raw.other || ""
                    : raw.value || ""
                : (raw ?? "");
        if (!val) return;
        if (f.type === "email" || lbl.includes("email")) out.email = val;
        else if (
            f.type === "phone" ||
            lbl.includes("contact") ||
            lbl.includes("phone") ||
            lbl.includes("mobile")
        )
            out.contact_number = val;
        else if (lbl === "name" || lbl.includes("full name")) out.name = val;
        else if (!out.name && lbl.includes("name") && !lbl.includes("college"))
            out.name = val;
        else if (lbl.includes("college")) out.college_name = val;
        else if (lbl.includes("year") && lbl.includes("graduation"))
            out.year_of_graduation = val;
        else if (lbl.includes("degree")) out.degree = val;
        else if (lbl.includes("course")) out.course = val;
    });
    return out;
}

// Normalise dynamic responses for wire — collapse {value, other} objects.
function flattenResponses(responses) {
    const out = {};
    Object.entries(responses || {}).forEach(([k, v]) => {
        if (v && typeof v === "object" && "value" in v) {
            out[k] = v.value === "Others" ? v.other || "" : v.value || "";
        } else {
            out[k] = v ?? "";
        }
    });
    return out;
}

export default function ApplyForm() {
    const { roleId } = useParams();
    const [role, setRole] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notAvailable, setNotAvailable] = useState(false);

    const [responses, setResponses] = useState({});
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [declared, setDeclared] = useState(false);

    // Duplicate handling state
    const [dupConfirmOpen, setDupConfirmOpen] = useState(false);
    const [dupInfoOpen, setDupInfoOpen] = useState(false);
    const [dupExistingId, setDupExistingId] = useState(null);
    const [dupMessage, setDupMessage] = useState("");
    const [dupExistingRole, setDupExistingRole] = useState("");
    const [dupNewRole, setDupNewRole] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            const { getCachedRoleForm, primeFormCache } = await import(
                "@/lib/publicCache"
            );
            const cached = getCachedRoleForm(roleId);
            if (cached && cached.form && alive) {
                setRole({ id: cached.id, name: cached.name });
                setForm(cached.form);
                setLoading(false);
                document.title = `Apply · ${cached.name} · BluBridge`;
            }
            try {
                const { data } = await api.get(
                    `/public/openings/${roleId}`,
                );
                if (!alive) return;
                if (!data || !data.form) {
                    if (!cached) setNotAvailable(true);
                    return;
                }
                setRole({ id: data.id, name: data.name });
                setForm(data.form);
                primeFormCache(roleId, data);
                document.title = `Apply · ${data.name} · BluBridge`;
            } catch (e) {
                if (!cached) {
                    toast.error(apiErrorMessage(e));
                    setNotAvailable(true);
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [roleId]);

    const updateResponse = (label, value) => {
        setResponses((r) => ({ ...r, [label]: value }));
        if (error) setError("");
    };

    const buildPayload = (existingId = null) => {
        const flat = flattenResponses(responses);
        const std = extractStandard(form.fields, responses);
        return {
            job_role_id: role.id,
            form_id: form.id,
            ...std,
            responses: flat,
            ...(existingId ? { existing_id: existingId } : {}),
        };
    };

    const validate = () => {
        const std = extractStandard(form.fields, responses);
        if (!std.email) return "Email field is required.";
        if (!CONTACT_RE.test(std.contact_number || "")) {
            return "Contact number must be exactly 10 digits, digits only, and cannot start with 0.";
        }
        // Required admin-defined fields
        for (const f of form.fields || []) {
            if (!f.required) continue;
            const raw = responses[f.label];
            const val =
                raw && typeof raw === "object" && "value" in raw
                    ? raw.value === "Others"
                        ? raw.other
                        : raw.value
                    : raw;
            if (!(val || "").toString().trim()) {
                return `${f.label} is required.`;
            }
        }
        if (!declared) {
            return "Please confirm the declaration to proceed.";
        }
        return null;
    };

    const doSubmit = async (existingId = null) => {
        setSubmitting(true);
        setError("");
        try {
            await api.post("/applicants", buildPayload(existingId));
            setSubmitted(true);
            toast.success(
                existingId
                    ? "Your previous application was updated."
                    : "Application submitted. We'll be in touch.",
            );
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data || {};
            if (status === 409 && data.type === "confirm") {
                setDupExistingId(data.existing_id);
                setDupMessage(data.detail);
                setDupExistingRole(
                    data.existing_role_name || "another job role",
                );
                setDupNewRole(
                    data.new_role_name || role?.name || "this job role",
                );
                setDupConfirmOpen(true);
            } else if (status === 409 && data.type === "info") {
                setDupMessage(data.detail);
                setDupInfoOpen(true);
            } else {
                setError(apiErrorMessage(err));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const v = validate();
        if (v) {
            setError(v);
            return;
        }
        doSubmit(null);
    };

    const confirmReplace = async () => {
        setDupConfirmOpen(false);
        await doSubmit(dupExistingId);
        setDupExistingId(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F7F5F0] text-[#0F1F38] font-sans flex items-center justify-center">
                <div className="flex items-center gap-3 text-sm text-[#0F1F38]/70">
                    <Loader2 size={16} className="animate-spin" />
                    Preparing your application form…
                </div>
            </div>
        );
    }

    if (notAvailable) {
        return (
            <div className="min-h-screen bg-[#F7F5F0] text-[#0F1F38] font-sans flex items-center justify-center px-6">
                <div className="max-w-md">
                    <div className="text-[10px] tracking-label uppercase text-[#0F1F38]/60 mb-3">
                        Application unavailable
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl leading-[1] text-[#0F1F38]">
                        This form isn&apos;t available.
                    </h1>
                    <p className="mt-4 text-[#0F1F38]/75">
                        The application form for this role hasn&apos;t been
                        published yet. Head back to{" "}
                        <a href="/" className="link-underline">
                            all openings
                        </a>
                        .
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid="apply-form-page"
            className="min-h-screen bg-[#F7F5F0] text-[#0F1F38] font-sans flex flex-col"
        >
            <header className="border-b border-[#0F1F38]/10 bg-[#F7F5F0]/95 backdrop-blur-xl sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 flex items-center justify-center">
                    <a href="/" className="inline-flex items-center">
                        <img
                            src="/blubridge-logo.png"
                            alt="BluBridge"
                            className="h-6 md:h-7 w-auto"
                        />
                    </a>
                </div>
            </header>

            <main className="flex-1 max-w-4xl w-full mx-auto px-4 md:px-8 py-8 md:py-12">
                <div
                    data-testid="apply-card"
                    className="relative bg-[#F7F5F0] border border-[#0F1F38]/25 rounded-2xl overflow-hidden"
                >
                    <div className="h-1.5 bg-[#0F1F38]" />
                    <div className="px-6 sm:px-10 md:px-14 py-8 md:py-10">
                        <h1 className="text-center font-display text-3xl md:text-4xl text-[#0F1F38] leading-tight">
                            {submitted ? "Thank You" : "Registration Form"}
                        </h1>

                        {submitted ? (
                                <div
                                    data-testid="apply-success"
                                    className="mt-8 text-center space-y-4"
                                >
                                    <p className="text-sm text-[#0F1F38]/75 max-w-md mx-auto">
                                        We&apos;ve received your application for{" "}
                                        <strong>{role.name}</strong>. Our Team
                                        will review it and reach out.
                                    </p>
                                    <a
                                        href="/"
                                        data-testid="apply-back-home"
                                        className="inline-flex items-center gap-2 px-5 h-11 mt-1 border border-[#0F1F38]/20 hover:border-[#0F1F38] text-xs tracking-label uppercase font-semibold transition-colors rounded-xl"
                                    >
                                        Back to openings
                                    </a>
                                </div>
                        ) : (
                            <form
                                onSubmit={onSubmit}
                                data-testid="apply-form"
                                className="mt-6 md:mt-8"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                                    {(form.fields || []).map((f, i) => {
                                        const wide =
                                            f.type === "textarea" ||
                                            f.type === "file";
                                        return (
                                            <div
                                                key={i}
                                                className={
                                                    wide ? "md:col-span-2" : ""
                                                }
                                            >
                                                <DynamicField
                                                    field={f}
                                                    idx={i}
                                                    value={
                                                        responses[f.label]
                                                    }
                                                    onChange={(v) =>
                                                        updateResponse(
                                                            f.label,
                                                            v,
                                                        )
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                {error && (
                                    <div
                                        data-testid="apply-error"
                                        className="mt-6 text-sm text-red-700 border-l-2 border-red-700 pl-3"
                                    >
                                        {error}
                                    </div>
                                )}

                                {/* Declaration checkbox — pale blue outlined box */}
                                <label
                                    htmlFor="apply-declaration"
                                    data-testid="apply-declaration-box"
                                    className={`mt-8 flex items-start gap-3 border rounded-xl px-4 py-3.5 cursor-pointer transition-colors ${
                                        declared
                                            ? "border-[#0F1F38] bg-[#F7F5F0]"
                                            : "border-[#0F1F38]/30 bg-[#F7F5F0]/60 hover:border-[#0F1F38]"
                                    }`}
                                >
                                    <input
                                        id="apply-declaration"
                                        type="checkbox"
                                        checked={declared}
                                        onChange={(e) => {
                                            setDeclared(e.target.checked);
                                            if (error) setError("");
                                        }}
                                        data-testid="apply-declaration-checkbox"
                                        className="mt-0.5 w-4 h-4 accent-[#0F1F38] cursor-pointer flex-shrink-0"
                                    />
                                    <span className="text-sm text-[#0F1F38] leading-relaxed">
                                        I hereby confirm that all the
                                        information provided above is accurate
                                        to the best of my knowledge.
                                    </span>
                                </label>

                                <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
                                    <a
                                        href="/"
                                        data-testid="apply-cancel"
                                        className="px-6 h-12 inline-flex items-center justify-center text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors rounded-xl"
                                    >
                                        Cancel
                                    </a>
                                    <button
                                        type="submit"
                                        data-testid="apply-submit"
                                        disabled={submitting || !declared}
                                        className="px-10 h-12 bg-[#0F1F38] text-white text-sm font-bold tracking-wider uppercase hover:bg-[#1A3254] transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 rounded-xl shadow-sm"
                                    >
                                        {submitting && (
                                            <Loader2
                                                size={16}
                                                className="animate-spin"
                                            />
                                        )}
                                        Submit
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </main>

            <footer className="mt-6 border-t border-[#0F1F38]/10 bg-[#0F1F38]">
                <div className="max-w-6xl mx-auto px-6 md:px-10 py-4 text-xs text-[#F7F5F0]/70">
                    Copyright {new Date().getFullYear()} ©{" "}
                    <a
                        href="https://blubridge.ai"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-[#F7F5F0] hover:underline"
                    >
                        Blubridge.com
                    </a>
                </div>
            </footer>

            {/* Case 1 — Confirm replace */}
            <Dialog open={dupConfirmOpen} onOpenChange={setDupConfirmOpen}>
                <DialogContent
                    data-testid="duplicate-confirm-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-lg p-8"
                >
                    <DialogHeader className="space-y-3">
                        <DialogTitle className="font-display text-2xl md:text-3xl leading-tight text-[#0F1F38]">
                            Existing application found
                        </DialogTitle>
                        <DialogDescription className="text-[15px] leading-relaxed text-[#0F1F38]/85">
                            You have already applied for{" "}
                            <span className="font-semibold text-[#0F1F38]">
                                {dupExistingRole}
                            </span>
                            ; If you want to change your job role to{" "}
                            <span className="font-semibold text-[#0F1F38]">
                                {dupNewRole}
                            </span>
                            , click Continue
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex items-center justify-end gap-3 pt-6">
                        <button
                            type="button"
                            onClick={() => setDupConfirmOpen(false)}
                            data-testid="duplicate-cancel"
                            className="px-5 h-11 text-xs tracking-label uppercase font-semibold border border-[#0F1F38]/20 hover:border-[#0F1F38] transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={confirmReplace}
                            data-testid="duplicate-continue"
                            className="px-6 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                        >
                            Continue
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Case 2 — Info only */}
            <Dialog open={dupInfoOpen} onOpenChange={setDupInfoOpen}>
                <DialogContent
                    data-testid="duplicate-info-dialog"
                    className="bg-[#F7F5F0] border-[#0F1F38]/20 rounded-none max-w-md"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl leading-tight text-[#0F1F38]">
                            Already applied
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#0F1F38]/80 pt-2">
                            {dupMessage ||
                                "You have already applied for this Job Role."}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex items-center justify-end pt-4">
                        <button
                            type="button"
                            onClick={() => setDupInfoOpen(false)}
                            data-testid="duplicate-info-ok"
                            className="px-6 h-11 bg-[#0F1F38] text-[#F7F5F0] text-xs tracking-label uppercase font-semibold hover:bg-[#1A3254] transition-colors"
                        >
                            OK
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Renders one dynamic field. Handles select with "Others" runtime behaviour.
function DynamicField({ field, idx, value, onChange }) {
    const isSelect = field.type === "select";
    const isDegree = field.type === "degree";
    const isChoice = isSelect || isDegree;
    const currentSelect = isChoice
        ? typeof value === "object" && value
            ? value
            : { value: "", other: "" }
        : null;
    const isOthers =
        isChoice && currentSelect && currentSelect.value === DEGREE_OTHERS;

    const label = (
        <Label className="text-sm font-medium text-[#0F1F38]">
            {field.label}
            {field.required && (
                <span className="text-red-700 ml-0.5">*</span>
            )}
            <span className="text-[#0F1F38]/50">:</span>
        </Label>
    );

    if (field.type === "textarea") {
        return (
            <div>
                {label}
                <Textarea
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`apply-dyn-${idx}`}
                    rows={4}
                    className="mt-2 rounded-md border-[#0F1F38]/25 bg-[#F7F5F0] resize-y focus-visible:ring-1 focus-visible:ring-[#0F1F38]/30 focus-visible:ring-offset-0"
                />
            </div>
        );
    }

    if (isDegree) {
        return (
            <div>
                {label}
                <DegreeSelect
                    value={currentSelect}
                    onChange={onChange}
                    testid={`apply-dyn-${idx}`}
                    surface="beige"
                />
                {isOthers && (
                    <Input
                        value={currentSelect.other || ""}
                        onChange={(e) =>
                            onChange({
                                ...currentSelect,
                                other: e.target.value,
                            })
                        }
                        placeholder="Type your degree (e.g. BE in Robotics)"
                        data-testid={`apply-dyn-other-${idx}`}
                        className="mt-2 h-11 rounded-xl border-[#0F1F38]/25 bg-[#F7F5F0]"
                    />
                )}
            </div>
        );
    }

    if (isSelect) {
        const options = field.options || [];
        return (
            <div>
                {label}
                <Select
                    value={currentSelect.value}
                    onValueChange={(v) =>
                        onChange({ ...currentSelect, value: v })
                    }
                >
                    <SelectTrigger
                        data-testid={`apply-dyn-${idx}`}
                        className="mt-2 h-11 rounded-lg border-[#0F1F38]/25 bg-[#F7F5F0]"
                    >
                        <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                        {options.map((o) => (
                            <SelectItem key={o} value={o}>
                                {o}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {isOthers && (
                    <Input
                        value={currentSelect.other || ""}
                        onChange={(e) =>
                            onChange({
                                ...currentSelect,
                                other: e.target.value,
                            })
                        }
                        placeholder="Please specify"
                        data-testid={`apply-dyn-other-${idx}`}
                        className="mt-2 h-11 rounded-xl border-[#0F1F38]/25 bg-[#F7F5F0]"
                    />
                )}
            </div>
        );
    }

    if (field.type === "boolean") {
        const val = value === "Yes" || value === "No" ? value : "";
        return (
            <div>
                {label}
                <div
                    data-testid={`apply-dyn-${idx}`}
                    className="mt-3 flex items-center gap-3"
                >
                    {["Yes", "No"].map((opt) => {
                        const active = val === opt;
                        return (
                            <button
                                key={opt}
                                type="button"
                                onClick={() => onChange(opt)}
                                data-testid={`apply-dyn-${idx}-${opt.toLowerCase()}`}
                                className={`min-w-[92px] h-11 px-5 border text-xs tracking-label uppercase font-semibold rounded-lg transition-colors ${
                                    active
                                        ? "bg-[#0F1F38] text-[#F7F5F0] border-[#0F1F38]"
                                        : "bg-[#F7F5F0] text-[#0F1F38] border-[#0F1F38]/25 hover:border-[#0F1F38]"
                                }`}
                            >
                                {opt}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    if (field.type === "date") {
        return (
            <div>
                {label}
                <Input
                    type="date"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={`apply-dyn-${idx}`}
                    className="mt-2 h-11 rounded-xl border-[#0F1F38]/25 bg-[#F7F5F0]"
                />
            </div>
        );
    }

    const inputType =
        field.type === "email"
            ? "email"
            : field.type === "url"
              ? "url"
              : field.type === "phone"
                ? "tel"
                : "text";
    const isPhone = field.type === "phone";
    const helper =
        field.type === "email"
            ? "Please enter a valid email address."
            : field.type === "phone"
              ? "Enter a 10-digit mobile number. Active WhatsApp preferred."
              : "";

    return (
        <div>
            {label}
            <Input
                type={inputType}
                inputMode={isPhone ? "numeric" : undefined}
                maxLength={isPhone ? 10 : undefined}
                value={value || ""}
                onChange={(e) => {
                    let v = e.target.value;
                    if (isPhone) v = v.replace(/\D+/g, "");
                    onChange(v);
                }}
                data-testid={`apply-dyn-${idx}`}
                className="mt-2 h-11 rounded-xl border-[#0F1F38]/25 bg-[#F7F5F0]"
            />
            {helper && (
                <p className="mt-1.5 text-xs text-[#0F1F38]/55">{helper}</p>
            )}
        </div>
    );
}
